import { z } from "zod";
import {
  FIREBASE_REGION,
  FIRESTORE_DATABASE_ID,
  PUBLIC_APP_URL,
  STORAGE_BUCKET_NAME,
  STORAGE_BUCKET_URL
} from "@/config/firebase.constants";

const optionalUrl = z.preprocess(
  (value) => (value === "" ? undefined : value),
  z.string().url().optional()
);
const envSchema = z.object({
  VITE_FIREBASE_API_KEY: z.string().min(1),
  VITE_FIREBASE_AUTH_DOMAIN: z.string().min(1),
  VITE_FIREBASE_PROJECT_ID: z.string().min(1),
  VITE_FIREBASE_STORAGE_BUCKET: z.literal(STORAGE_BUCKET_NAME),
  VITE_FIREBASE_MESSAGING_SENDER_ID: z.string().min(1),
  VITE_FIREBASE_APP_ID: z.string().min(1),
  VITE_FIREBASE_MEASUREMENT_ID: z.string().optional(),
  VITE_FIRESTORE_DATABASE_ID: z.literal(FIRESTORE_DATABASE_ID),
  VITE_FIREBASE_STORAGE_URL: z.literal(STORAGE_BUCKET_URL),
  VITE_GEOANALYTICS_PUBLIC_URL: optionalUrl
});

type GeoAnalyticsViteEnv = {
  VITE_FIREBASE_API_KEY?: string;
  VITE_FIREBASE_AUTH_DOMAIN?: string;
  VITE_FIREBASE_PROJECT_ID?: string;
  VITE_FIREBASE_STORAGE_BUCKET?: string;
  VITE_FIREBASE_MESSAGING_SENDER_ID?: string;
  VITE_FIREBASE_APP_ID?: string;
  VITE_FIREBASE_MEASUREMENT_ID?: string;
  VITE_FIRESTORE_DATABASE_ID?: string;
  VITE_FIREBASE_STORAGE_URL?: string;
  VITE_GEOANALYTICS_PUBLIC_URL?: string;
};

const viteEnv = import.meta.env as unknown as GeoAnalyticsViteEnv;

const rawEnv = {
  VITE_FIREBASE_API_KEY: viteEnv.VITE_FIREBASE_API_KEY,
  VITE_FIREBASE_AUTH_DOMAIN: viteEnv.VITE_FIREBASE_AUTH_DOMAIN,
  VITE_FIREBASE_PROJECT_ID: viteEnv.VITE_FIREBASE_PROJECT_ID,
  VITE_FIREBASE_STORAGE_BUCKET: viteEnv.VITE_FIREBASE_STORAGE_BUCKET,
  VITE_FIREBASE_MESSAGING_SENDER_ID: viteEnv.VITE_FIREBASE_MESSAGING_SENDER_ID,
  VITE_FIREBASE_APP_ID: viteEnv.VITE_FIREBASE_APP_ID,
  VITE_FIREBASE_MEASUREMENT_ID: viteEnv.VITE_FIREBASE_MEASUREMENT_ID,
  VITE_FIRESTORE_DATABASE_ID: viteEnv.VITE_FIRESTORE_DATABASE_ID ?? FIRESTORE_DATABASE_ID,
  VITE_FIREBASE_STORAGE_URL: viteEnv.VITE_FIREBASE_STORAGE_URL ?? STORAGE_BUCKET_URL,
  VITE_GEOANALYTICS_PUBLIC_URL: viteEnv.VITE_GEOANALYTICS_PUBLIC_URL
};

const parsedEnv = envSchema.safeParse(rawEnv);

if (!parsedEnv.success) {
  const missing = parsedEnv.error.issues.map((issue) => issue.path.join(".")).join(", ");
  throw new Error(
    `GeoAnalytics configuration error. Required environment variables are invalid or missing: ${missing}`
  );
}

export const env = {
  firebase: {
    apiKey: parsedEnv.data.VITE_FIREBASE_API_KEY,
    authDomain: parsedEnv.data.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: parsedEnv.data.VITE_FIREBASE_PROJECT_ID,
    storageBucket: parsedEnv.data.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: parsedEnv.data.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: parsedEnv.data.VITE_FIREBASE_APP_ID,
    measurementId: parsedEnv.data.VITE_FIREBASE_MEASUREMENT_ID,
    firestoreDatabaseId: parsedEnv.data.VITE_FIRESTORE_DATABASE_ID,
    storageUrl: parsedEnv.data.VITE_FIREBASE_STORAGE_URL,
    region: FIREBASE_REGION
  },
  app: {
    publicUrl: parsedEnv.data.VITE_GEOANALYTICS_PUBLIC_URL ?? PUBLIC_APP_URL
  }
} as const;
